import { Checkbox } from '@mantine/core'
import * as questionnaires from '@seen/core/models/questionnaires/index'
import { ValidatedTool } from '@seen/core/models/validatedTool'
import type { ValidatedToolResponse } from '@seen/core/models/validatedToolResponse'
import { Badge } from '@seen/ui'
import type { JSX } from 'react'
import { match } from 'ts-pattern'
import { z } from 'zod'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { FormSubmitButton } from '#src/components/core/form/FormSubmitButton.tsx'
import { Input } from '#src/components/core/input/Input.tsx'
import { useValidatedToolForm } from '#src/components/validated-tool-form/shared/useValidatedToolForm.ts'
import {
  ValidatedToolInnerForm,
  type ValidatedToolInnerFormProps,
} from '#src/components/validated-tool-form/shared/ValidatedToolInnerForm.tsx'
import { ValidatedToolSection } from '#src/components/validated-tool-form/shared/ValidatedToolSection.tsx'
import type { UseFormReturn } from '#src/hooks/useForm.ts'

export interface MOCAFormProps extends Omit<
  ValidatedToolInnerFormProps,
  'validatedTool' | 'form' | 'subtitle'
> {
  response?: ValidatedToolResponse
  restorationId?: string
}

const schema = z.object({
  '/moca-visuospatial': z.number().int().min(0).max(5).nullable(),
  '/moca-naming': z.number().int().min(0).max(3).nullable(),
  '/moca-attention': z.number().int().min(0).max(6).nullable(),
  '/moca-language': z.number().int().min(0).max(3).nullable(),
  '/moca-abstraction': z.number().int().min(0).max(2).nullable(),
  '/moca-delayed-recall': z.number().int().min(0).max(5).nullable(),
  '/moca-orientation': z.number().int().min(0).max(6).nullable(),
  '/moca-education-adjustment': z.boolean().nullable(),
  '/moca-mis': z.number().int().min(0).max(15).nullable(),
  '/72133-2': z.number().nullable(),
})

const SCORED_SECTIONS = [
  {
    linkId: '/moca-visuospatial',
    label: 'VISUOSPATIAL / EXECUTIVE',
    max: 5,
  },
  { linkId: '/moca-naming', label: 'NAMING', max: 3 },
  { linkId: '/moca-attention', label: 'ATTENTION', max: 6 },
  { linkId: '/moca-language', label: 'LANGUAGE', max: 3 },
  { linkId: '/moca-abstraction', label: 'ABSTRACTION', max: 2 },
  {
    linkId: '/moca-delayed-recall',
    label: 'DELAYED RECALL',
    max: 5,
  },
  { linkId: '/moca-orientation', label: 'ORIENTATION', max: 6 },
] as const

const validatedTool = new ValidatedTool(questionnaires.mocaQuestionnaire)

export function MOCAForm({
  response,
  restorationId,
  ...delegated
}: MOCAFormProps): JSX.Element {
  const form = useValidatedToolForm({
    schema,
    validatedTool,
    response,
    restorationId,
  })

  return (
    <ValidatedToolInnerForm
      validatedTool={validatedTool}
      form={form}
      response={response}
      {...delegated}
    >
      <ValidatedToolSection title="Scores">
        <div className="grid gap-4">
          {SCORED_SECTIONS.map(({ linkId, label, max }) => (
            <div key={linkId}>
              <FormField
                control={form.control}
                name={linkId}
                render={({ field: { value, onChange, ...rest } }) => (
                  <div className="flex items-center gap-2">
                    <label className="w-52 text-sm font-medium">{label}</label>
                    <Input
                      value={value ?? ''}
                      onChange={(e) => {
                        const num = e.target.valueAsNumber
                        onChange(Number.isNaN(num) ? null : num)
                      }}
                      type="number"
                      min={0}
                      max={max}
                      className="w-20"
                      {...rest}
                    />
                    <span className="text-sm text-muted-foreground">
                      / {max}
                    </span>
                  </div>
                )}
              />
              {linkId === '/moca-delayed-recall' && (
                <FormField
                  control={form.control}
                  name="/moca-mis"
                  render={({ field: { value, onChange, ...rest } }) => (
                    <div className="mt-2 ml-6 flex items-center gap-2">
                      <label className="w-44 text-sm text-muted-foreground">
                        Memory Index Score (MIS)
                      </label>
                      <Input
                        value={value ?? ''}
                        onChange={(e) => {
                          const num = e.target.valueAsNumber
                          onChange(Number.isNaN(num) ? null : num)
                        }}
                        type="number"
                        min={0}
                        max={15}
                        className="w-20"
                        {...rest}
                      />
                      <span className="text-sm text-muted-foreground">
                        / 15
                      </span>
                    </div>
                  )}
                />
              )}
            </div>
          ))}

          <FormField
            control={form.control}
            name="/moca-education-adjustment"
            render={({ field: { value, ...rest } }) => (
              <Checkbox
                {...rest}
                onChange={(e) => rest.onChange(e.target.checked)}
                checked={!!value}
                label="≤12 years of formal education (+1 point)"
              />
            )}
          />
        </div>
      </ValidatedToolSection>

      <Score form={form} response={response} />

      {!response && (
        <div>
          <FormSubmitButton control={form.control}>Submit</FormSubmitButton>
        </div>
      )}
    </ValidatedToolInnerForm>
  )
}

function Score({
  form,
  response,
}: {
  form: UseFormReturn<any>
  response?: ValidatedToolResponse
}): JSX.Element {
  const values = form.watch()

  const total = response
    ? (response.getAnswerValueNumber('/72133-2') ?? response.score)
    : computeTotal(values)

  const { color, label } = match(total)
    .when(
      (s) => s >= 26,
      () => ({ color: 'green' as const, label: 'Normal' })
    )
    .when(
      (s) => s >= 18,
      () => ({ color: 'yellow' as const, label: 'Mild Cognitive Impairment' })
    )
    .when(
      (s) => s >= 10,
      () => ({
        color: 'orange' as const,
        label: 'Moderate Cognitive Impairment',
      })
    )
    .otherwise(() => ({
      color: 'red' as const,
      label: 'Severe Cognitive Impairment',
    }))

  return (
    <div className="flex items-center gap-2">
      <input
        type="hidden"
        {...form.register('/72133-2', {
          value: total,
        })}
      />
      <h2>
        Score: <b>{total}</b>
      </h2>
      <Badge color={color} className="mt-px">
        {label}
      </Badge>
    </div>
  )
}

function computeTotal(values: z.infer<typeof schema>): number {
  const sectionSum = SCORED_SECTIONS.reduce(
    (acc, { linkId }) => acc + (values[linkId] ?? 0),
    0
  )
  const educationAdjustment = values['/moca-education-adjustment'] ? 1 : 0
  return Math.min(30, sectionSum + educationAdjustment)
}
